// frontend/src/pages/admin/ShopAdmin.js
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './ShopAdmin.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

function ShopAdmin({ companyId }) {
  const [activeTab, setActiveTab] = useState('products');
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [accessList, setAccessList] = useState([]);
  const [orders, setOrders] = useState([]);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      await Promise.all([
        fetchProducts(),
        fetchCategories(),
        fetchAccessList(),
        fetchOrders(),
        fetchSettings()
      ]);
    } catch (err) {
      setError('Ошибка загрузки данных');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const fetchProducts = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/shop/products`, {
        params: { company_id: companyId }
      });
      setProducts(response.data);
    } catch (err) {
      console.error('Ошибка загрузки товаров:', err);
    }
  }, [companyId]);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/shop/categories`, {
        params: { company_id: companyId }
      });
      setCategories(response.data);
    } catch (err) {
      console.error('Ошибка загрузки категорий:', err);
    }
  }, [companyId]);

  const fetchAccessList = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/shop/access`, {
        params: { company_id: companyId }
      });
      setAccessList(response.data);
    } catch (err) {
      console.error('Ошибка загрузки доступа:', err);
    }
  }, [companyId]);

  const fetchOrders = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/shop/orders`, {
        params: { company_id: companyId }
      });
      setOrders(response.data);
    } catch (err) {
      console.error('Ошибка загрузки заказов:', err);
      setOrders([]);
    }
  }, [companyId]);

  const fetchSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/shop/settings`, {
        params: { company_id: companyId }
      });
      setSettings(response.data);
    } catch (err) {
      console.error('Ошибка загрузки настроек:', err);
    }
  }, [companyId]);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setLoading(true);
    setError('');
    setSuccess('');
    
    const formData = new FormData();
    formData.append('company_id', companyId);
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API_URL}/api/admin/shop/products/upload`, formData);
      setSuccess(`✅ ${response.data.message}`);
      await fetchProducts();
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка загрузки');
    } finally {
      setLoading(false);
      e.target.value = '';
    }
  };

  const toggleAccess = async (employeeId, currentStatus) => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('is_allowed', !currentStatus);
      await axios.post(`${API_URL}/api/admin/shop/access/${employeeId}/toggle`, formData);
      await fetchAccessList();
      setSuccess('✅ Доступ обновлён');
    } catch (err) {
      setError('Ошибка обновления доступа');
    } finally {
      setLoading(false);
    }
  };

  const updateSettings = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      const formData = new FormData();
      formData.append('company_id', companyId);
      Object.keys(settings).forEach(key => {
        if (settings[key] !== null && settings[key] !== undefined) {
          formData.append(key, settings[key]);
        }
      });
      
      await axios.put(`${API_URL}/api/admin/shop/settings`, formData);
      setSuccess('✅ Настройки сохранены');
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка сохранения');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="shop-admin">
      <div className="shop-admin-tabs">
        <button className={activeTab === 'products' ? 'active' : ''} onClick={() => setActiveTab('products')}>
          📦 Товары
        </button>
        <button className={activeTab === 'categories' ? 'active' : ''} onClick={() => setActiveTab('categories')}>
          📂 Категории
        </button>
        <button className={activeTab === 'access' ? 'active' : ''} onClick={() => setActiveTab('access')}>
          🔑 Доступ
        </button>
        <button className={activeTab === 'orders' ? 'active' : ''} onClick={() => setActiveTab('orders')}>
          📋 Заказы
        </button>
        <button className={activeTab === 'stickers' ? 'active' : ''} onClick={() => setActiveTab('stickers')}>
          🏷️ Наклейки
        </button>
        <button className={activeTab === 'export' ? 'active' : ''} onClick={() => setActiveTab('export')}>
          📥 Выгрузка
        </button>
        <button className={activeTab === 'settings' ? 'active' : ''} onClick={() => setActiveTab('settings')}>
          ⚙️ Настройки
        </button>
      </div>

      <div className="shop-admin-content">
        {activeTab === 'products' && (
          <ProductsTab 
            products={products}
            categories={categories}
            onUpload={handleFileUpload}
            loading={loading}
            error={error}
            success={success}
          />
        )}
        {activeTab === 'categories' && (
          <CategoriesTab 
            categories={categories}
            companyId={companyId}
            onRefresh={fetchCategories}
          />
        )}
        {activeTab === 'access' && (
          <AccessTab 
            accessList={accessList}
            onToggle={toggleAccess}
            loading={loading}
          />
        )}
        {activeTab === 'orders' && (
          <OrdersTab orders={orders} />
        )}
        {activeTab === 'stickers' && (
          <StickersTab companyId={companyId} />
        )}
        {activeTab === 'export' && (
          <ShopExportTab companyId={companyId} />
        )}
        {activeTab === 'settings' && (
          <SettingsTab 
            settings={settings}
            setSettings={setSettings}
            onSave={updateSettings}
            loading={loading}
            error={error}
            success={success}
          />
        )}
      </div>
    </div>
  );
}

// ============================================================
// ВКЛАДКА: ТОВАРЫ
// ============================================================

function ProductsTab({ products, categories, onUpload, loading, error, success }) {
  return (
    <div className="shop-tab-panel">
      <h3>📦 Товары</h3>
      
      <div className="shop-upload-section">
        <p>Загрузить товары из Excel:</p>
        <input type="file" accept=".xlsx,.xls" onChange={onUpload} />
        <small>Формат: Название, Категория, Цена, Единица измерения</small>
      </div>
      
      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}
      
      {loading && <div className="loading-spinner"></div>}
      
      <div className="shop-products-list">
        {products.length === 0 ? (
          <p>Нет товаров. Загрузите Excel-файл.</p>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Название</th>
                  <th>Категория</th>
                  <th>Цена</th>
                  <th>Ед.</th>
                </tr>
              </thead>
              <tbody>
                {products.map(p => (
                  <tr key={p.id}>
                    <td>{p.name}</td>
                    <td>{p.category_name || '—'}</td>
                    <td>{p.price} ₽</td>
                    <td>{p.unit}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================
// ВКЛАДКА: КАТЕГОРИИ
// ============================================================

function CategoriesTab({ categories, companyId, onRefresh }) {
  const [newName, setNewName] = useState('');
  const [newIcon, setNewIcon] = useState('📦');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleCreate = async () => {
    if (!newName.trim()) return;
    
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      const formData = new FormData();
      formData.append('company_id', companyId);
      formData.append('name', newName);
      formData.append('icon', newIcon);
      
      await axios.post(`${API_URL}/api/admin/shop/categories`, formData);
      setSuccess('✅ Категория создана');
      setNewName('');
      onRefresh();
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка создания');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Удалить категорию?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/admin/shop/categories/${id}`);
      setSuccess('✅ Категория удалена');
      onRefresh();
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка удаления');
    }
  };

  return (
    <div className="shop-tab-panel">
      <h3>📂 Категории</h3>
      
      <div className="shop-category-form">
        <input 
          type="text" 
          placeholder="Название категории" 
          value={newName} 
          onChange={e => setNewName(e.target.value)}
        />
        <input 
          type="text" 
          placeholder="Иконка (например, 🥩)" 
          value={newIcon} 
          onChange={e => setNewIcon(e.target.value)}
          style={{ width: '60px' }}
        />
        <button onClick={handleCreate} disabled={loading || !newName.trim()}>
          ➕ Добавить
        </button>
      </div>
      
      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}
      
      <div className="shop-categories-list">
        {categories.map(c => (
          <div key={c.id} className="shop-category-item">
            <span>{c.icon} {c.name}</span>
            <span>Товаров: {c.product_count}</span>
            <button onClick={() => handleDelete(c.id)} className="btn-danger">🗑️</button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================
// ВКЛАДКА: ДОСТУП
// ============================================================

function AccessTab({ accessList, onToggle, loading }) {
  return (
    <div className="shop-tab-panel">
      <h3>🔑 Доступ к магазину</h3>
      
      <p>По умолчанию доступ выключен. Включите для нужных сотрудников.</p>
      
      <div className="shop-access-list">
        {accessList.length === 0 ? (
          <p>Нет сотрудников</p>
        ) : (
          accessList.map(emp => (
            <div key={emp.employee_id} className="shop-access-item">
              <span>{emp.full_name}</span>
              <span>{emp.phone}</span>
              <span className={emp.is_allowed ? 'access-on' : 'access-off'}>
                {emp.is_allowed ? '✅ Доступ есть' : '❌ Доступа нет'}
                {emp.auto_blocked && ' 🔒 (автоблокировка)'}
              </span>
              <button 
                onClick={() => onToggle(emp.employee_id, emp.is_allowed)}
                disabled={loading}
              >
                {emp.is_allowed ? '🔒 Отключить' : '🔓 Включить'}
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// ============================================================
// ВКЛАДКА: ЗАКАЗЫ
// ============================================================

function OrdersTab({ orders }) {
  const getStatusLabel = (status) => {
    const labels = {
      'cart': '🟡 В корзине',
      'awaiting_payment': '🟣 Ожидает оплаты',
      'awaiting_verification': '🟠 На проверке',
      'paid': '🟢 Оплачен',
      'delivered': '🔵 Выдан',
      'cancelled': '🔴 Отменён'
    };
    return labels[status] || status;
  };

  return (
    <div className="shop-tab-panel">
      <h3>📋 Заказы</h3>
      
      {orders.length === 0 ? (
        <p>Нет заказов</p>
      ) : (
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>№</th>
                <th>Сотрудник</th>
                <th>Сумма</th>
                <th>Статус</th>
                <th>Дата</th>
              </tr>
            </thead>
            <tbody>
              {orders.map(o => (
                <tr key={o.id}>
                  <td>{o.order_number}</td>
                  <td>{o.employee_name}</td>
                  <td>{o.total_amount} ₽</td>
                  <td>{getStatusLabel(o.status)}</td>
                  <td>{new Date(o.created_at).toLocaleDateString('ru-RU')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ============================================================
// ВКЛАДКА: НАКЛЕЙКИ
// ============================================================

function StickersTab({ companyId }) {
  const [date, setDate] = useState('');
  const [generating, setGenerating] = useState(false);
  const [generatingAll, setGeneratingAll] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfUrlAll, setPdfUrlAll] = useState(null);
  const [error, setError] = useState('');
  const [resultInfo, setResultInfo] = useState(null);

  const handleGenerate = async () => {
    if (!date) { setError('Выберите дату'); return; }
    setGenerating(true); setError(''); setPdfUrl(null);
    try {
      const formData = new FormData();
      formData.append('company_id', companyId);
      formData.append('date', date);
      const response = await axios.post(`${API_URL}/api/admin/shop/stickers`, formData);
      setPdfUrl(`${API_URL}${response.data.pdf_url}`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка');
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerateAllDays = async () => {
    setGeneratingAll(true); setError(''); setPdfUrlAll(null); setResultInfo(null);
    try {
      const formData = new FormData();
      formData.append('company_id', companyId);
      const response = await axios.post(`${API_URL}/api/admin/shop/stickers/all-days`, formData);
      setPdfUrlAll(`${API_URL}${response.data.pdf_url}`);
      setResultInfo({
        orders: response.data.orders_count,
        dates: response.data.dates_count
      });
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка');
    } finally {
      setGeneratingAll(false);
    }
  };

  return (
    <div className="shop-tab-panel">
      <h3>🏷️ Наклейки (коммерческие заказы)</h3>
      
      <div className="card">
        <h4>📅 По дате</h4>
        <p className="form-hint">Создаёт PDF с наклейками для оплаченных коммерческих заказов на выбранную дату.</p>
        <div className="filter-row" style={{ marginTop: 12 }}>
          <input type="date" className="input" value={date} onChange={(e) => setDate(e.target.value)} />
          <button className={`btn ${generating ? 'loading' : ''}`} onClick={handleGenerate} disabled={generating}>
            {generating ? <span className="spinner"></span> : '🏷️ Создать PDF'}
          </button>
        </div>
        {pdfUrl && (
          <div className="success-message" style={{ marginTop: 12 }}>
            ✅ PDF готов!<br />
            <a href={pdfUrl} target="_blank" rel="noopener noreferrer" className="btn btn-sm" style={{ marginTop: 8, display: 'inline-flex' }}>
              📄 Открыть PDF
            </a>
          </div>
        )}
      </div>

      <div className="card" style={{ borderColor: 'var(--primary)' }}>
        <h4>📆 За все дни</h4>
        <p className="form-hint">Создаёт один PDF с наклейками для ВСЕХ оплаченных коммерческих заказов.</p>
        <div className="filter-row" style={{ marginTop: 12 }}>
          <button 
            className={`btn btn-success ${generatingAll ? 'loading' : ''}`} 
            onClick={handleGenerateAllDays} 
            disabled={generatingAll}
            style={{ background: 'linear-gradient(135deg, #10b981, #059669)' }}
          >
            {generatingAll ? <span className="spinner"></span> : '📆 Создать PDF за все дни'}
          </button>
        </div>
        {resultInfo && (
          <div className="success-message" style={{ marginTop: 12 }}>
            ✅ PDF готов! <br />
            📊 {resultInfo.dates} дней, {resultInfo.orders} наклеек
          </div>
        )}
        {pdfUrlAll && (
          <div className="success-message" style={{ marginTop: 12 }}>
            <a href={pdfUrlAll} target="_blank" rel="noopener noreferrer" className="btn btn-sm" style={{ display: 'inline-flex' }}>
              📄 Открыть PDF
            </a>
          </div>
        )}
      </div>

      {error && <div className="error-message">{error}</div>}
    </div>
  );
}

// ============================================================
// ВКЛАДКА: ВЫГРУЗКА
// ============================================================

function ShopExportTab({ companyId }) {
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [status, setStatus] = useState('');
  const [employeeName, setEmployeeName] = useState('');
  const [exporting, setExporting] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [ordersCount, setOrdersCount] = useState(null);
  const [error, setError] = useState('');

  const handleExport = async () => {
    setExporting(true);
    setError('');
    setDownloadUrl(null);
    setOrdersCount(null);

    try {
      const formData = new FormData();
      formData.append('company_id', companyId);
      if (dateFrom) formData.append('date_from', dateFrom);
      if (dateTo) formData.append('date_to', dateTo);
      if (status) formData.append('status', status);
      if (employeeName) formData.append('employee_name', employeeName);

      const response = await axios.post(`${API_URL}/api/admin/shop/export`, formData);
      setDownloadUrl(`${API_URL}${response.data.download_url}`);
      setOrdersCount(response.data.orders_count);
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка выгрузки');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="shop-tab-panel">
      <h3>📥 Выгрузка коммерческих заказов</h3>

      <div className="card">
        <div className="filter-row" style={{ flexWrap: 'wrap' }}>
          <input
            type="date"
            className="input"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            placeholder="С даты"
            style={{ flex: 1, minWidth: '120px' }}
          />
          <input
            type="date"
            className="input"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            placeholder="По дату"
            style={{ flex: 1, minWidth: '120px' }}
          />
          <select
            className="input select-input"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            style={{ flex: 1, minWidth: '140px' }}
          >
            <option value="">Все статусы</option>
            <option value="awaiting_payment">Ожидает оплаты</option>
            <option value="awaiting_verification">На проверке</option>
            <option value="paid">Оплачен</option>
            <option value="delivered">Выдан</option>
            <option value="cancelled">Отменён</option>
          </select>
          <input
            type="text"
            className="input"
            value={employeeName}
            onChange={(e) => setEmployeeName(e.target.value)}
            placeholder="Сотрудник"
            style={{ flex: 1, minWidth: '140px' }}
          />
          <button
            className={`btn ${exporting ? 'loading' : ''}`}
            onClick={handleExport}
            disabled={exporting}
          >
            {exporting ? <span className="spinner"></span> : '📥 Скачать Excel'}
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {downloadUrl && (
        <div className="success-message">
          ✅ Выгружено <strong>{ordersCount}</strong> заказов
          <br />
          <a href={downloadUrl} target="_blank" rel="noopener noreferrer" className="btn btn-sm" style={{ marginTop: 8, display: 'inline-flex' }}>
            📄 Скачать файл
          </a>
        </div>
      )}
    </div>
  );
}

// ============================================================
// ВКЛАДКА: НАСТРОЙКИ
// ============================================================

function SettingsTab({ settings, setSettings, onSave, loading, error, success }) {
  return (
    <div className="shop-tab-panel">
      <h3>⚙️ Настройки магазина</h3>
      
      <div className="shop-settings-form">
        <label>
          <input 
            type="checkbox" 
            checked={settings.module_enabled} 
            onChange={e => setSettings({...settings, module_enabled: e.target.checked})}
          />
          Модуль магазина включён
        </label>
        
        <label>
          <input 
            type="checkbox" 
            checked={settings.default_access} 
            onChange={e => setSettings({...settings, default_access: e.target.checked})}
          />
          Доступ по умолчанию (для новых сотрудников)
        </label>
        
        <label>
          Максимальная сумма заказа:
          <input 
            type="number" 
            value={settings.max_order_amount || ''} 
            onChange={e => setSettings({...settings, max_order_amount: e.target.value || null})}
            placeholder="Без ограничений"
          />
        </label>
        
        <label>
          <input 
            type="checkbox" 
            checked={settings.notify_admin} 
            onChange={e => setSettings({...settings, notify_admin: e.target.checked})}
          />
          Уведомлять администратора
        </label>
        
        <label>
          <input 
            type="checkbox" 
            checked={settings.notify_delivery} 
            onChange={e => setSettings({...settings, notify_delivery: e.target.checked})}
          />
          Уведомлять доставку
        </label>
        
        <label>
          <input 
            type="checkbox" 
            checked={settings.notify_kitchen} 
            onChange={e => setSettings({...settings, notify_kitchen: e.target.checked})}
          />
          Уведомлять кухню
        </label>
        
        <label>
          Email для уведомлений (админ):
          <input 
            type="email" 
            value={settings.email_admin || ''} 
            onChange={e => setSettings({...settings, email_admin: e.target.value || null})}
            placeholder="admin@moyobed.pro"
          />
        </label>
      </div>
      
      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}
      
      <button onClick={onSave} disabled={loading}>
        💾 Сохранить настройки
      </button>
    </div>
  );
}

export default ShopAdmin;
